import { useQuery } from '@apollo/client';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { LotteryModuleResult, Module } from '@thro-types/modules-selections';
import { ShibUser } from '@thro-types/users';

import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import USERS_QUERY from 'gql/queries/getUsers.gql';

import GQLGuard from 'app/components/GQLGuard';
import { SubjectType, UsersQueryArgs } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { userDataToLecturerOption } from 'app/containers/AWPM-FWPM/helper';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { Course } from 'types/AppTypes';
import { UserType } from 'utils/ACLHelper';

type Props = {
  module: Module;
  courses: Course[];
  lotteryResults: LotteryModuleResult | undefined;
  subjectType: SubjectType;
  isLoading: boolean;
  shouldShowAssignedCount: boolean;
};

export const GeneralInfo = ({
  module,
  courses,
  lotteryResults,
  subjectType,
  isLoading,
  shouldShowAssignedCount
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const usersQuery = useQuery<any, UsersQueryArgs>(USERS_QUERY, {
    variables: {
      type: UserType.SHIBBOLETH,
      ids: [...module.lecturers, module.moduleOfficer ? module.moduleOfficer : 0],
      limit: 0
    },
    context: { clientName: 'federation' }
  });

  const getModuleOfficer = (lecturers: ShibUser[]) => {
    const moduleOfficer = lecturers.find(lecturer => lecturer.id === module.moduleOfficer);
    return !!moduleOfficer ? userDataToLecturerOption(moduleOfficer, t).label : null;
  };

  const getModuleLecturer = (lecturers: ShibUser[]) => [
    ...(!!module.lecturers?.length
      ? module.lecturers?.map(
          lecturer =>
            userDataToLecturerOption(lecturers.find(data => data.id === lecturer)!, t).label
        )
      : []),
    ...(!!module.unregisteredLecturers.length ? module.unregisteredLecturers : [])
  ];

  return (
    <GQLGuard
      query={usersQuery}
      onError={error => {
        if (error?.graphQLErrors[0]?.extensions?.status === 404) {
          dispatch(AlertActions.error(`${t(translations.pages.modules.alerts.failedLoading())}`));
        } else {
          dispatch(
            AlertActions.error(
              `${t(translations.pages.modules.alerts.failedLoading())}: ${error.message}`
            )
          );
        }
        navigate('/', { replace: true });
        return null;
      }}
    >
      {lecturers => {
        const lecturerUsers: ShibUser[] = lecturers.users;
        return (
          <Grid
            container
            md={3}
            className="moduleDetailView__generalInfo"
            gap={1}
            alignContent={'flex-start'}
            style={!isMobile ? { backgroundColor: 'lightgrey' } : undefined}
          >
            {!module.isTakingPlace && !module.isDefault && (
              <Grid item xs={12} style={{ paddingBottom: '1em' }}>
                <Typography component="h2" color="error">
                  <DoNotDisturbAltIcon /> {'Findet dieses Semester nicht statt.'}
                </Typography>
              </Grid>
            )}
            {subjectType === SubjectType.Fwpm && (
              <Grid item xs={12}>
                <Typography component="h2">{t(translations.pages.modules.faculty())}</Typography>
                <Typography component="h3">
                  {courses.find(item => item.id === module.responsibleCourseOfStudy)?.name}
                </Typography>
              </Grid>
            )}
            {!!module.moduleOfficer && (
              <Grid item xs={12}>
                <Typography component="h2">
                  {t(translations.pages.modules.moduleResponsible())}
                </Typography>
                <Typography component="h3">{getModuleOfficer(lecturerUsers)}</Typography>
              </Grid>
            )}
            {(!!module.lecturers.length || !!module.unregisteredLecturers.length) && (
              <Grid item xs={12}>
                <Typography component="h2">{t(translations.pages.modules.lecturers())}</Typography>
                <Typography component="h3">
                  {getModuleLecturer(lecturerUsers).reduce(
                    (previous, current) => previous + ', ' + current
                  )}
                </Typography>
              </Grid>
            )}
            {!!module.minParticipants && (
              <Grid item xs={12}>
                <Typography component="h2">
                  {t(translations.pages.modules.create.periodsAndParticipants.participants.min())}
                </Typography>
                <Typography component="h3">{module.minParticipants}</Typography>
              </Grid>
            )}
            {!!module.maxParticipants && (
              <Grid item xs={12}>
                <Typography component="h2">
                  {t(translations.pages.modules.create.periodsAndParticipants.participants.max())}
                </Typography>
                <Typography component="h3">{module.maxParticipants}</Typography>
              </Grid>
            )}
            {!!lotteryResults && shouldShowAssignedCount && (
              <Grid item xs={12}>
                <Typography component="h2">
                  {t(translations.pages.modules.registeredParticipants())}
                </Typography>
                {isLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <Typography component="h3">{lotteryResults?.assigned.length}</Typography>
                )}
              </Grid>
            )}
          </Grid>
        );
      }}
    </GQLGuard>
  );
};
